/**
 * The work you started, wherever you have gone since ([[ROADMAP A.30]]).
 *
 * A rebalance comparison takes minutes and a large risk analysis can take
 * longer. Both pages told the reader they could leave — and then offered no way
 * to see the work from anywhere else, so leaving meant losing sight of it. This
 * is the thing that makes that sentence true.
 *
 * Three rules from the research, and each one is a decision here:
 *
 * 1. **Name the state, not the spinner.** Running, ready, or failed — with the
 *    reason. "Failed" with no sentence is "Something went wrong" in a smaller
 *    box, and a bare spinner is indistinguishable from a hang.
 * 2. **Restore, never restart.** The state comes from the server, so it
 *    survives a reload, a second tab, and a page that was never mounted when
 *    the work began.
 * 3. **Announce the finish.** A result that landed while the reader was
 *    elsewhere is the case this exists for; it is not enough to have it waiting
 *    to be discovered.
 */
import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, IconButton, Paper, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { typography } from '../../common/styles/universalTheme';
import { useAuth } from '../../contexts/AuthContext';
import { useActiveJobsStore, visibleJobs, type ActiveJob } from '../../stores/activeJobsStore';

/** Where a finished job's answer lives.
 *
 * The address of the ANSWER when the job produced one, not the page that
 * produces it ([[BUGS #275]]: *"Ready — open it"* landed on the run page, not
 * on the comparison that had just finished). A rebalance archives its result
 * under its own id and every result has been addressable since the report page
 * gained `/portfolio/rebalance/:reportId`; a risk analysis has no such id —
 * its result is a saved report the job never names — so that route keeps its
 * bare form and the page resolves the reader's latest.
 */
const destinationFor = (job: ActiveJob): string => {
  const base = job.kind === 'rebalance' ? '/portfolio/rebalance' : '/portfolio/risk-results';
  return job.reportId ? `${base}/${encodeURIComponent(job.reportId)}` : base;
};

const elapsed = (startedAt: string): string => {
  const started = new Date(startedAt).getTime();
  if (!Number.isFinite(started)) return '';
  const secs = Math.max(0, Math.round((Date.now() - started) / 1000));
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
};

const ActiveWorkIndicator: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { jobs, dismissed, start, stop, reset, dismiss } = useActiveJobsStore();

  const userId = user?.uid;
  const getToken = useCallback(async () => (user ? user.getIdToken() : ''), [user]);

  useEffect(() => {
    if (!userId) { reset(); return; }
    start(getToken);
    return () => stop();
    // `user?.uid`, not `user`: the context hands back a fresh object each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const shown = visibleJobs(jobs, dismissed);
  if (shown.length === 0) return null;

  return (
    <Box
      // Fixed to the viewport, because the whole point is that it does not
      // belong to any one page. Bottom-left keeps it clear of the chat launcher.
      sx={{
        position: 'fixed', left: { xs: 12, md: 76 }, bottom: 16, zIndex: 1200,
        display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 'min(92vw, 380px)',
      }}
      aria-live="polite"
    >
      {shown.map((job) => {
        const done = job.status === 'done';
        const failed = job.status === 'failed';
        return (
          <Paper
            key={job.id}
            variant="outlined"
            sx={{ p: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1.25,
                  bgcolor: 'background.paper' }}
          >
            <Box sx={{ mt: '2px', display: 'flex' }}>
              {job.status === 'running' && <CircularProgress size={16} sx={{ color: 'text.primary' }} />}
              {done && <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main' }} />}
              {failed && <ErrorOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />}
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: typography.fontSize.sm, color: 'text.primary',
                                fontWeight: typography.fontWeight.medium }}>
                {job.kindLabel}
                {job.label ? ` · ${job.label}` : ''}
              </Typography>

              {job.status === 'running' && (
                <Typography sx={{ fontSize: typography.fontSize.xs, color: 'text.secondary' }}>
                  {/* Elapsed rather than a percentage: we do not know how far
                      through it is, and inventing a bar would be a lie the
                      reader can time. */}
                  Running{elapsed(job.startedAt) ? ` · ${elapsed(job.startedAt)}` : ''} — you can
                  keep working; this finishes on its own.
                </Typography>
              )}

              {done && (
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => { dismiss(job.id); navigate(destinationFor(job)); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault(); dismiss(job.id); navigate(destinationFor(job));
                    }
                  }}
                  sx={{ cursor: 'pointer', display: 'inline-block' }}
                >
                  <Typography sx={{ fontSize: typography.fontSize.xs, color: 'text.primary',
                                    textDecoration: 'underline' }}>
                    Ready — open it
                  </Typography>
                </Box>
              )}

              {failed && (
                <Typography sx={{ fontSize: typography.fontSize.xs, color: 'text.secondary' }}>
                  {job.message || 'It could not be completed. Please try again.'}
                </Typography>
              )}
            </Box>

            {job.status !== 'running' && (
              <IconButton size="small" aria-label={`Dismiss ${job.kindLabel}`}
                          onClick={() => dismiss(job.id)}
                          sx={{ color: 'text.secondary', mt: '-2px' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Paper>
        );
      })}
    </Box>
  );
};

export default ActiveWorkIndicator;
